import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  GridAggregationModel,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  Toolbar,
  ToolbarButton,
  gridColumnLookupSelector,
  gridColumnReorderDragColSelector,
  gridRowGroupingSanitizedModelSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import {
  Alert, Autocomplete, Box, Chip, IconButton,
  Menu, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import MoveUpIcon from "@mui/icons-material/MoveUp";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettlementForm from "./SettlementForm";
import NoteModal from "./NoteModal";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faChevronDown, faChevronRight, faChevronUp, faPlus, faXmark, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Search from "assets/icons/search.gif";
import bankingService from "api/bankingService";
import { IFinancialOperation } from "dto/Banking/IBanking";
import InfoIcon from "assets/icons/info_icon.svg?react";
import FilterIcon from "assets/icons/tune.svg?react";
import DownloadIcon from "assets/icons/download.svg?react";
import highlightText from "utils/highlightText";
import { plGridLocales } from "utils/muiGridLocales";
import { generateFinancialOperationsPdf } from "utils/generateFinancialOperationsPdf";
import ConnectBankModal from "./ConnectBankModal";
import FinancialOperationMetricSlide from "./FinancialOperationMetricSlide";
import { FC, Props } from "./typings";
import useGridRowSelection from "hooks/useGridRowSelection";
import DocTypeSelect from "components/Registry/DocTypeSelect";
import { docScopeOf, docSubtitleLabel } from "components/Registry/DocTypeSelect/docTypes";
import PeriodPicker from "components/Registry/PeriodPicker";
import { useSchemeMatcher } from "hooks/useSchemeMatcher";
import styles from "./styles.module.scss";

// ── Stałe ────────────────────────────────────────────────────────────────────

const GRID_KEY       = "grid_financial_operations_v1";
const LS_FILTERS_KEY = "financialOps_savedFilters";
const CURRENT_YEAR   = new Date().getFullYear();
const YEAR_OPTIONS   = Array.from({ length: 7 }, (_, i) => CURRENT_YEAR - i);

type FinOpScope  = "all" | "credit" | "debit";

const SCOPE_LABELS: Record<FinOpScope, string> = {
  all: "Wszystkie", credit: "Wpływy", debit: "Wydatki",
};

interface SavedFilter { Id: string; Name: string; }

interface FinOpFilters { bank: string[]; counterparty: string[]; status: string[]; }
const EMPTY_FILTERS: FinOpFilters = { bank: [], counterparty: [], status: [] };

const BRAK = "Brak";

const buildOptions = (values: string[]): string[] => {
  const nonEmpty = [...new Set(values.filter((v) => v !== ""))].sort((a, b) => a.localeCompare(b, "pl"));
  const hasEmpty = values.some((v) => v === "");
  if (nonEmpty.length === 0) return [BRAK];
  return hasEmpty ? [...nonEmpty, BRAK] : nonEmpty;
};

// ── Pomocnicze ────────────────────────────────────────────────────────────────

const toISO = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayISO = () => toISO(new Date());

const plDate = (iso: string) => iso ? new Date(iso).toLocaleDateString("pl-PL") : "";

const formatCurrency = (value: number, currency = "PLN"): string =>
  new Intl.NumberFormat("pl-PL", { style: "currency", currency: currency || "PLN" }).format(value);

const formatIban = (iban?: string) =>
  iban ? iban.replace(/(.{4})/g, "$1 ").trim() : "";

// Próba wydobycia kontrahenta z tytułu dla transakcji kartowych
const resolveCounterparty = (op: IFinancialOperation): string => {
  if (op.CounterpartyName) return op.CounterpartyName;
  if (!op.Title) return "";
  return op.Title.split(/[/|\\;]/)[0].trim();
};

// ── Komórka IBAN z przyciskiem kopiowania ─────────────────────────────────────

const IbanCell = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const raw = (value || "").replace(/\s/g, "");
    navigator.clipboard.writeText(raw).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (!value) return null;

  return (
    <div className={styles.ibanWrapper}>
      <span className={styles.ibanCell} title={value}>{value}</span>
      <Tooltip title={copied ? "Skopiowano!" : "Kopiuj numer rachunku"} placement="top">
        <button className={`${styles.ibanCopyBtn} ${copied ? styles.ibanCopied : ""}`} onClick={handleCopy}>
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
      </Tooltip>
    </div>
  );
};

// ── Nakładka "brak danych" ────────────────────────────────────────────────────

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Brak operacji finansowych. Podłącz rachunek bankowy, aby pobrać dane.</div>
  </div>
);

// ── Typ kotwicy popupu ────────────────────────────────────────────────────────

type PopupPos = { top: number; left: number } | null;

// ── Główny komponent ──────────────────────────────────────────────────────────

const FinancialOperationsRegister: FC<Props> = ({ searchText, setSubtitle, docType = "bank:all", onDocTypeChange }) => {
  const apiRef = useGridApiRef();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  const [connectOpen, setConnectOpen] = useState(false);
  const [metricOf, setMetricOf] = useState<IFinancialOperation | null>(null);
  const [actionMenu, setActionMenu] = useState<{ el: HTMLElement; row: IFinancialOperation } | null>(null);
  const [settlementOf, setSettlementOf] = useState<IFinancialOperation | null>(null);
  const [noteOf, setNoteOf] = useState<IFinancialOperation | null>(null);

  // Audyt schematów księgowania. Transakcja bankowa: wpłata (Credit) = kontrahent
  // jako nabywca → „Sale"; wypłata (Debit) = kontrahent jako sprzedawca → „Purchase".
  // Dopasowanie po ContractorId (operacje bankowe są powiązane z kartoteką).
  const matchScheme = useSchemeMatcher();

  // ── Stan siatki ─────────────────────────────────────────────────────────
  const storedState = (): GridInitialState => {
    try {
      const raw = localStorage.getItem(GRID_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      // Nie przywracaj otwartego panelu zarządzania kolumnami — domyślnie ma być zamknięty.
      if (parsed?.preferencePanel) delete parsed.preferencePanel;
      return parsed;
    } catch {
      return {};
    }
  };

  const [initialState] = useState<GridInitialState>(storedState);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => storedState()?.columns?.columnVisibilityModel ?? {});
  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>(
    () => storedState()?.rowGrouping?.model ?? []);
  const [aggregationModel, setAggregationModel] = useState<GridAggregationModel>({});

  // ── Stan paska funkcji ───────────────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [preset, setPreset] = useState<string | null>("biezacyRok");
  const [from, setFrom] = useState(`${CURRENT_YEAR}-01-01`);
  const [to, setTo] = useState(todayISO());
  // Zakres transakcji wyprowadzony z „Typ dokumentu" (bank:all|credit|debit).
  const bankScope = docScopeOf(docType);
  const scopeFilter: FinOpScope = (["all", "credit", "debit"].includes(bankScope) ? bankScope : "all") as FinOpScope;
  const [periodPopupPos, setPeriodPopupPos] = useState<PopupPos>(null);
  const [savedFiltersPopupPos, setSavedFiltersPopupPos] = useState<PopupPos>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FinOpFilters>(EMPTY_FILTERS);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState(false);

  const toggleSection = (key: string) =>
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleFilter = (field: keyof FinOpFilters, value: string) =>
    setActiveFilters((prev) => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });

  useEffect(() => {
    try { setSavedFilters(JSON.parse(localStorage.getItem(LS_FILTERS_KEY) || "[]")); } catch { /* */ }
  }, []);

  const persistFilters = (list: SavedFilter[]) => {
    setSavedFilters(list);
    localStorage.setItem(LS_FILTERS_KEY, JSON.stringify(list));
  };

  const activeFilterCount = useMemo(() =>
    (activeFilters.bank.length > 0 ? 1 : 0) +
    (activeFilters.counterparty.length > 0 ? 1 : 0) +
    (activeFilters.status.length > 0 ? 1 : 0),
  [activeFilters]);

  // ── Dane ─────────────────────────────────────────────────────────────────
  const { data, isLoading } = bankingService.useGetOperations(searchText);
  const { data: connections } = bankingService.useGetConnections();
  const { data: aspsps } = bankingService.useGetAspsps("PL", true);
  const refresh = bankingService.useRefresh();

  const hasExpired = useMemo(
    () => Array.isArray(connections) && connections.some((c) => c.Status === "Expired"),
    [connections]);

  const logoMap = useMemo(() => {
    const map: Record<string, string> = {};
    (aspsps ?? []).forEach((a) => { if (a.LogoUrl) map[a.Name] = a.LogoUrl; });
    return map;
  }, [aspsps]);

  const filterOptions = useMemo(() => {
    const all = Array.isArray(data) ? data : [];
    return {
      bank:         buildOptions(all.map((o) => o.AspspName || "")),
      counterparty: buildOptions(all.map((o) => resolveCounterparty(o))),
      status:       [BRAK],
    };
  }, [data]);

  const saveSnapshotInternal = () => {
    try { localStorage.setItem(GRID_KEY, JSON.stringify(apiRef.current.exportState())); }
    catch { /* localStorage niedostępny */ }
  };

  // ── Filtrowanie wierszy ──────────────────────────────────────────────────
  const rows = useMemo(() => {
    const all = Array.isArray(data) ? data : [];
    return all
      .filter((o) => {
        if (scopeFilter === "credit" && o.Direction !== "Credit") return false;
        if (scopeFilter === "debit"  && o.Direction === "Credit") return false;
        if (from && o.BookingDate && o.BookingDate < from) return false;
        if (to   && o.BookingDate && o.BookingDate > `${to}T23:59:59`) return false;
        if (activeFilters.bank.length > 0) {
          const val = o.AspspName || "";
          if (!activeFilters.bank.includes(val) && !(activeFilters.bank.includes(BRAK) && !val)) return false;
        }
        if (activeFilters.counterparty.length > 0) {
          const val = resolveCounterparty(o);
          if (!activeFilters.counterparty.includes(val) && !(activeFilters.counterparty.includes(BRAK) && !val)) return false;
        }
        return true;
      })
      .map((o) => ({ id: o.Id, ...o }));
  }, [data, scopeFilter, from, to, activeFilters]);
  rowsRef.current = rows;

  // ── Podtytuł ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const scope = docSubtitleLabel(docType);
    const period = from || to ? `${plDate(from)} – ${plDate(to)}` : "";
    setSubtitle(period ? `${scope} · ${period}` : scope);
  }, [docType, from, to, setSubtitle]);

  // ── PDF ──────────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (downloading) return;
    const selected = apiRef.current.getSelectedRows();
    const selectedIds = new Set(selected.keys());
    const toExport = rows.filter((r) => selectedIds.size === 0 || selectedIds.has(r.id));
    if (toExport.length === 0) return;
    setDownloading(true);
    try {
      await generateFinancialOperationsPdf({
        rows: toExport.map((r) => ({
          bookingDate:  r.BookingDate ?? "",
          bank:         r.AspspName ?? "",
          account:      r.CounterpartyIban ? formatIban(r.CounterpartyIban) : "",
          counterparty: resolveCounterparty(r as IFinancialOperation),
          title:        r.Title ?? "",
          amount:       r.Amount,
          currency:     r.Currency,
          direction:    r.Direction,
        })),
        from, to, scopeLabel: SCOPE_LABELS[scopeFilter],
      });
    } finally { setDownloading(false); }
  };

  // ── Kolumny ──────────────────────────────────────────────────────────────
  const columns: GridColDef[] = useMemo(() => [
    {
      field: "BookingDate",
      headerName: "Data",
      width: 110,
      renderHeader: () => <div className={styles.header}>Data</div>,
      valueFormatter: (v) => (v ? moment(v).format("DD.MM.YYYY") : ""),
    },
    {
      field: "AspspName",
      headerName: "Bank",
      width: 140,
      renderHeader: () => <div className={styles.header}>Bank</div>,
      renderCell: (params: GridRenderCellParams) => {
        const name = params.value as string;
        const logo = logoMap[name];
        return (
          <div className={styles.bankCell}>
            {logo && <img src={logo} className={styles.bankLogo} alt="" />}
            <span className={styles.cell} title={name}>{name}</span>
          </div>
        );
      },
    },
    {
      field: "CounterpartyIban",
      headerName: "Rachunek",
      width: 200,
      renderHeader: () => <div className={styles.header}>Rachunek</div>,
      valueGetter: (_, row: IFinancialOperation) =>
        row.CounterpartyIban ? formatIban(row.CounterpartyIban) : "",
      renderCell: (params: GridRenderCellParams) => (
        <IbanCell value={params.value} />
      ),
    },
    {
      field: "CounterpartyName",
      headerName: "Kontrahent",
      flex: 1,
      minWidth: 160,
      renderHeader: () => <div className={styles.header}>Kontrahent</div>,
      valueGetter: (_, row: IFinancialOperation) => resolveCounterparty(row),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as IFinancialOperation;
        const hasCounterparty = !!(row.CounterpartyName || row.CounterpartyIban);
        const unlinked = hasCounterparty && !row.ContractorId;
        return (
          <div className={styles.counterpartyCell}>
            {unlinked && (
              <Tooltip title="Brak powiązania z kartoteką kontrahentów" placement="top">
                <span className={styles.unlinkedDot} />
              </Tooltip>
            )}
            <span
              className={styles.cell}
              title={params.value}
              dangerouslySetInnerHTML={{ __html: highlightText(params.value ?? "", searchText ?? "") }}
            />
          </div>
        );
      },
    },
    {
      field: "Title",
      headerName: "Tytuł",
      flex: 1,
      minWidth: 180,
      renderHeader: () => <div className={styles.header}>Tytuł</div>,
      renderCell: (params: GridRenderCellParams) => (
        <div
          className={styles.cell}
          title={params.value}
          dangerouslySetInnerHTML={{ __html: highlightText(params.value ?? "", searchText ?? "") }}
        />
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 120,
      renderHeader: () => <div className={styles.header}>Status</div>,
      valueGetter: () => "",
    },
    {
      field: "amount",
      headerName: "Kwota",
      width: 150,
      type: "number",
      aggregable: true,
      renderHeader: () => <div className={styles.header}>Kwota</div>,
      valueGetter: (_, row: IFinancialOperation) =>
        row.Direction === "Credit" ? row.Amount : -row.Amount,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return <></>;
        const row = params.row as IFinancialOperation;
        const currency = row?.Currency ?? "PLN";
        const isCredit = row?.Direction === "Credit";
        const absAmount = Math.abs(params.value as number);
        return isCredit
          ? <span className={styles.credit}>{formatCurrency(absAmount, currency)}</span>
          : <span className={styles.debit}>−{formatCurrency(absAmount, currency)}</span>;
      },
    },
    {
      field: "BalanceAfter",
      headerName: "Saldo",
      width: 130,
      type: "number",
      renderHeader: () => <div className={styles.header}>Saldo</div>,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return <></>;
        const currency = (params.row as IFinancialOperation)?.Currency ?? "PLN";
        return <span>{formatCurrency(params.value, currency)}</span>;
      },
    },
    {
      field: "metric",
      headerName: "Met.",
      width: 70,
      sortable: false,
      filterable: false,
      groupable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
      align: "center",
      headerAlign: "center",
      renderHeader: () => <div className={styles.header}>Met.</div>,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") return <></>;
        const row = params.row as IFinancialOperation;
        if (!row?.Id) return null;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {matchScheme({ contractorId: row?.ContractorId, direction: row?.Direction === "Credit" ? "Sale" : "Purchase" }) && (
              <Tooltip title="Schemat księgowania ustalony dla tego kontrahenta" placement="left">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "#2e7d32",
                    boxShadow: "0 0 0 2px #d5f0d8",
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title="Metryka operacji" placement="left">
              <span
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 4, cursor: "pointer" }}
                onClick={() => setMetricOf(row)}
              >
                <InfoIcon style={{ width: 18, height: 18 }} />
              </span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 56,
      sortable: false,
      filterable: false,
      groupable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
      align: "center",
      headerAlign: "center",
      renderHeader: () => <></>,
      renderCell: (params: GridRenderCellParams) => {
        if (params.rowNode.type === "group") return <></>;
        const row = params.row as IFinancialOperation;
        if (!row?.Id) return null;
        return (
          <Tooltip title="Funkcje" placement="left">
            <IconButton size="small" onClick={(e) => setActionMenu({ el: e.currentTarget, row })}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ], [searchText, logoMap, matchScheme]);

  useEffect(() => {
    setAggregationModel(groupingModel.length > 0 ? { amount: "sum" } : {});
  }, [groupingModel]);

  // ── Toolbar ───────────────────────────────────────────────────────────────
  // Zdefiniowany wewnątrz komponentu dla dostępu do stanu; popupy renderowane
  // w JSX rodzica (poza DataGrid) aby unikać problemów z re-montowaniem slotów.
  const FinancialOperationsToolbar = () => {
    const gridApiRef = useGridApiContext();
    const rowGroupingModel = useGridSelector(gridApiRef, gridRowGroupingSanitizedModelSelector);
    const columnsLookup    = useGridSelector(gridApiRef, gridColumnLookupSelector);
    const draggedColumn    = useGridSelector(gridApiRef, gridColumnReorderDragColSelector);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (draggedColumn && !rowGroupingModel.includes(draggedColumn) && columnsLookup[draggedColumn]?.groupable !== false)
        gridApiRef.current.addRowGroupingCriteria(draggedColumn);
    };

    const removeGroup = (field: string) => {
      if (columnsLookup[field]?.groupable !== false)
        gridApiRef.current.removeRowGroupingCriteria(field);
    };

    const openPopup = (e: React.MouseEvent, setter: (p: PopupPos) => void, current: PopupPos) => {
      if (current) { setter(null); return; }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setter({ top: rect.bottom + 4, left: rect.left });
    };

    return (
      <Toolbar>
        <div className={styles.mergedToolbar}>
          <Tooltip title="Podłącz rachunek bankowy" placement="bottom">
            <button className={styles.addBtn} onClick={() => setConnectOpen(true)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </Tooltip>

          <Tooltip title="Odśwież operacje z banku" placement="bottom">
            <button
              className={styles.addBtn}
              disabled={refresh.isLoading}
              onClick={() => refresh.mutate()}
            >
              <FontAwesomeIcon icon={faArrowsRotate} spin={refresh.isLoading} />
            </button>
          </Tooltip>

          <div className={styles.groupingSection} onDragOver={handleDragOver}>
            <MoveUpIcon fontSize="small" color="action" sx={{ mr: 0.75, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
              {rowGroupingModel.length === 0 ? (
                <Typography variant="body2" color="textSecondary" noWrap>
                  Przeciągnij kolumnę tutaj aby zgrupować
                </Typography>
              ) : (
                rowGroupingModel.map((field, idx) => (
                  <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                    {idx > 0 && <ChevronRightIcon fontSize="small" color="action" />}
                    <Chip
                      label={columnsLookup[field]?.headerName ?? field}
                      size="small"
                      onDelete={() => removeGroup(field)}
                      sx={{ cursor: "grab" }}
                    />
                  </Box>
                ))
              )}
            </Box>
          </div>

          <div className={styles.toolbarRight}>
            <Tooltip title={downloading ? "Generowanie…" : "Pobierz PDF zaznaczonych"} placement="bottom">
              <button
                className={`${styles.filterBtn} ${downloading ? styles.filterBtnActive : ""}`}
                onClick={handleDownload}
                disabled={downloading}
              >
                <DownloadIcon />
              </button>
            </Tooltip>

            <div
              className={styles.savedFiltersBtn}
              onClick={(e) => openPopup(e, setPeriodPopupPos, periodPopupPos)}
            >
              Okres
              <FontAwesomeIcon icon={periodPopupPos ? faChevronUp : faChevronDown} />
            </div>

            <DocTypeSelect value={docType} onChange={(v) => onDocTypeChange?.(v)} />


            <div
              className={styles.savedFiltersBtn}
              onClick={(e) => openPopup(e, setSavedFiltersPopupPos, savedFiltersPopupPos)}
            >
              Twoje filtry
              <FontAwesomeIcon icon={savedFiltersPopupPos ? faChevronUp : faChevronDown} />
            </div>

            <Tooltip title="Filtry" placement="bottom-end">
              <button
                className={`${styles.filterBtn} ${filterOpen ? styles.filterBtnActive : ""}`}
                onClick={() => setFilterOpen((v) => !v)}
              >
                <FilterIcon />
                {activeFilterCount > 0 && <span className={styles.filterBadge}>{activeFilterCount}</span>}
              </button>
            </Tooltip>

            <Tooltip title="Kolumny" placement="bottom-end">
              <ColumnsPanelTrigger render={<ToolbarButton sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: "4px", padding: "4px 8px", color: "#5f5c71", minWidth: "unset", "&:hover": { borderColor: "#5d49c7", background: "rgba(93,73,199,0.04)", color: "#5d49c7" } }} />}>
                <ViewColumnIcon fontSize="small" />
              </ColumnsPanelTrigger>
            </Tooltip>
          </div>
        </div>
      </Toolbar>
    );
  };

  return (
    <div className={styles.wrapper}>
      {hasExpired && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>
          Zgoda na dostęp do jednego z rachunków wygasła. Podłącz rachunek ponownie,
          aby kontynuować pobieranie operacji.
        </Alert>
      )}

      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          initialState={initialState}
          localeText={plGridLocales}
          slots={{ toolbar: FinancialOperationsToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          loading={isLoading}
          columnHeaderHeight={30}
          columns={columns}
          rows={rows}
          hideFooter
          disableColumnFilter
          disablePivoting
          disableAggregation={false}
          columnVisibilityModel={columnVisibilityModel}
          aggregationModel={aggregationModel}
          slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
          }}
          onColumnVisibilityModelChange={(m) => { setColumnVisibilityModel(m); setTimeout(saveSnapshotInternal, 1000); }}
          onColumnOrderChange={() => { setTimeout(saveSnapshotInternal, 1000); }}
          onRowGroupingModelChange={(m) => { setGroupingModel(m); setTimeout(saveSnapshotInternal, 1000); }}
          onAggregationModelChange={() => { setTimeout(saveSnapshotInternal, 1000); }}
        />
      </div>

      {/* ── Popup: Okres ─────────────────────────────────────────────────── */}
      {periodPopupPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setPeriodPopupPos(null)} />
          <div className={styles.periodDropdown} style={{ position: "fixed", top: periodPopupPos.top, left: periodPopupPos.left, zIndex: 1400 }}>
            <PeriodPicker
              year={selectedYear}
              from={from}
              to={to}
              preset={preset}
              yearOptions={YEAR_OPTIONS}
              setYear={setSelectedYear}
              setFrom={setFrom}
              setTo={setTo}
              setPreset={setPreset}
            />
          </div>
        </>,
        document.body,
      )}

      {/* ── Popup: Twoje filtry ──────────────────────────────────────────── */}
      {savedFiltersPopupPos && createPortal(
        <>
          <div className={styles.popupBackdrop} onClick={() => setSavedFiltersPopupPos(null)} />
          <div className={styles.savedFiltersDropdown} style={{ position: "fixed", top: savedFiltersPopupPos.top, left: savedFiltersPopupPos.left, zIndex: 1400 }}>
            {savedFilters.length === 0 && <div className={styles.savedFiltersEmpty}>Brak zapisanych filtrów</div>}
            {savedFilters.map((f) => (
              <div key={f.Id} className={styles.savedFilterItem} onClick={() => setSavedFiltersPopupPos(null)}>
                <span className={styles.savedFilterName}>{f.Name}</span>
                <button className={styles.savedFilterDel} onClick={(e) => { e.stopPropagation(); persistFilters(savedFilters.filter((x) => x.Id !== f.Id)); }}>✕</button>
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}

      {/* ── Panel filtru (slide z prawej) ────────────────────────────────── */}
      {filterOpen && createPortal(
        <div className={styles.overlay} onClick={() => setFilterOpen(false)}>
          <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.panelHeader}>
              <span className={styles.panelTitle}>Filtry</span>
              <div className={styles.panelHeaderActions}>
                {activeFilterCount > 0 && (
                  <button className={styles.resetBtn} onClick={() => setActiveFilters(EMPTY_FILTERS)}>
                    Wyczyść
                  </button>
                )}
                <button className={styles.closeBtn} onClick={() => setFilterOpen(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              {(["bank", "counterparty", "status"] as (keyof FinOpFilters)[]).map((field) => {
                const labels: Record<keyof FinOpFilters, string> = {
                  bank: "Bank", counterparty: "Kontrahent", status: "Status",
                };
                const options = filterOptions[field];
                const selected = activeFilters[field];
                const collapsed = collapsedSections[field] ?? false;
                return (
                  <div key={field} className={styles.filterSection}>
                    <button className={styles.filterSectionHeader} onClick={() => toggleSection(field)}>
                      <span className={styles.filterLabel}>
                        {labels[field]}
                        {selected.length > 0 && (
                          <span className={styles.filterSectionBadge}>{selected.length}</span>
                        )}
                      </span>
                      <FontAwesomeIcon
                        icon={collapsed ? faChevronRight : faChevronDown}
                        className={styles.filterChevron}
                      />
                    </button>
                    {!collapsed && (
                      <>
                        {selected.length > 0 && (
                          <div className={styles.filterChipGroup}>
                            {selected.map((opt) => (
                              <Chip
                                key={opt}
                                label={opt}
                                size="small"
                                onDelete={() => toggleFilter(field, opt)}
                                sx={{
                                  fontStyle: opt === BRAK ? "italic" : "normal",
                                  background: "#5d49c7",
                                  color: "#fff",
                                  "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "#fff" } },
                                }}
                              />
                            ))}
                          </div>
                        )}
                        <Autocomplete
                          multiple
                          size="small"
                          options={options.filter((o) => !selected.includes(o))}
                          value={selected}
                          onChange={(_, newVal) =>
                            setActiveFilters((prev) => ({ ...prev, [field]: newVal }))
                          }
                          disableCloseOnSelect
                          renderTags={() => null}
                          renderOption={(props, opt) => {
                            const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
                            return (
                              <li key={key} {...rest} style={{ fontSize: 13, fontStyle: opt === BRAK ? "italic" : "normal" }}>
                                {opt}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`Szukaj ${labels[field].toLowerCase()}…`}
                              sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 13 },
                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#5d49c7" },
                              }}
                            />
                          )}
                          noOptionsText="Brak wyników"
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}

      <ConnectBankModal open={connectOpen} onClose={() => setConnectOpen(false)} />

      {metricOf && (
        <FinancialOperationMetricSlide operation={metricOf} onClose={() => setMetricOf(null)} />
      )}

      {/* Przybornik funkcji operacji (kolumna ⋮) */}
      <Menu
        anchorEl={actionMenu?.el ?? null}
        open={!!actionMenu}
        onClose={() => setActionMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (actionMenu) setSettlementOf(actionMenu.row);
            setActionMenu(null);
          }}
        >
          Rozrachunek
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) setNoteOf(actionMenu.row);
            setActionMenu(null);
          }}
        >
          Dodaj notatkę
        </MenuItem>
      </Menu>

      {settlementOf && (
        <SettlementForm operation={settlementOf} onClose={() => setSettlementOf(null)} />
      )}
      {noteOf && (
        <NoteModal operation={noteOf} onClose={() => setNoteOf(null)} />
      )}
    </div>
  );
};

export default FinancialOperationsRegister;
